/**
 * /dashboard — Customizable, drag/drop/resize dashboard.
 *
 * Uses react-grid-layout. Persists per-user layout coords to the DB
 * on drag/resize release. Reset button restores the SAA default.
 *
 * Widgets:
 *   quote_banner   · "Master the play. Know when to run it."
 *   block_tracker  · 3.5-Hour Daily Block Tracker (compact)
 *   ixl_radar      · IXL Mastery Radar
 *   combine        · SAA Combine card
 *   sport_iq       · Continue Coach B's course
 *   socratic_ai    · Ask Coach (Socratic AI Assistant)
 *   per_analytics  · PER Analytics
 *   forum_feed     · Latest Playbook Boards threads
 *   calendar       · Travel/Absence upcoming
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveGridLayout } from 'react-grid-layout';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, Target, TrendingUp, MessageSquare, Zap, RefreshCcw, ArrowRight,
  Clock, GraduationCap, Brain, CalendarDays, Trophy, MessagesSquare, Lock, Unlock, GripVertical,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGrid = ResponsiveGridLayout;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

// ─── Individual widgets ─────────────────────────────────────────────────────
const WidgetShell = ({ title, icon: Icon, accent = '#FF5722', children, onOpen, cta, unlocked }) => (
  <div className="w-full h-full flex flex-col bg-zinc-950 border-2 border-zinc-800 overflow-hidden">
    <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-900 bg-zinc-950 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        {unlocked && <span className="drag-handle cursor-move text-zinc-600 hover:text-orange-500 flex-shrink-0"><GripVertical className="w-3.5 h-3.5" /></span>}
        {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />}
        <span className="text-[10px] font-black uppercase tracking-widest text-white truncate">{title}</span>
      </div>
      {cta && (
        <button onClick={(e) => { e.stopPropagation(); onOpen?.(); }} className="text-[9px] font-black uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1 flex-shrink-0">
          {cta} <ArrowRight className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
    <div className="flex-1 overflow-auto p-3 flex flex-col min-h-0">{children}</div>
  </div>
);

// 60/40 phrase rotation: SAA (Learn How. Know When.) 60% · SOLO (Master the play.
// Know when to run it.) 40%. Rolled once per page load based on session hash so
// students get variety without churn during a single visit.
const BANNER_PHRASES = [
  { headline: 'Master The Play', quote: '"Learn How. Know When."', tag: 'Applied Mastery · The SAA Standard' },                   // SAA
  { headline: 'Master The Play', quote: '"Learn How. Know When."', tag: 'Applied Mastery · The SAA Standard' },                   // SAA
  { headline: 'Master The Play', quote: '"Learn How. Know When."', tag: 'Applied Mastery · The SAA Standard' },                   // SAA
  { headline: 'Master The Play', quote: '"Know when to run it."',    tag: 'Self-Optimized · The SOLO Standard' },                  // SOLO
  { headline: 'Master The Play', quote: '"Know when to run it."',    tag: 'Self-Optimized · The SOLO Standard' },                  // SOLO
];
const _bannerPick = BANNER_PHRASES[Math.floor(Math.random() * BANNER_PHRASES.length)];

const QuoteBanner = ({ unlocked }) => (
  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-r from-orange-500/10 via-transparent to-amber-500/10 border-2 border-orange-500/40 p-6 text-center relative overflow-hidden">
    <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle at 30% 20%, #FF5722 0%, transparent 40%)' }} />
    {unlocked && <span className="drag-handle cursor-move absolute top-2 left-2 text-zinc-500 hover:text-orange-500"><GripVertical className="w-4 h-4" /></span>}
    <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] text-white relative">{_bannerPick.headline}</h2>
    <p className="text-lg md:text-2xl font-black uppercase tracking-tight text-orange-500 mt-3 relative">{_bannerPick.quote}</p>
    <p className="text-[10px] text-zinc-400 uppercase tracking-widest mt-4 relative">{_bannerPick.tag}</p>
  </div>
);

const BlockTracker = ({ nav, unlocked }) => (
  <WidgetShell title="3.5-Hour Block" icon={Clock} unlocked={unlocked} onOpen={() => nav('/academy/school')} cta="Open">
    <div className="text-3xl font-black text-white">0:00 <span className="text-sm text-zinc-500">/ 3:30</span></div>
    <div className="mt-2 w-full h-1.5 bg-zinc-900">
      <div className="h-full bg-orange-500" style={{ width: '0%' }} />
    </div>
    <button onClick={() => nav('/academy/school')} className="mt-auto border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest w-full">Start Today's Block</button>
  </WidgetShell>
);

const IXLRadar = ({ nav, unlocked }) => (
  <WidgetShell title="IXL Mastery Radar" icon={TrendingUp} accent="#22C55E" unlocked={unlocked} onOpen={() => nav('/academy/school')} cta="Open">
    <div className="grid grid-cols-2 gap-2 text-[11px]">
      {['Math', 'ELA', 'Science', 'Social Studies'].map((s) => (
        <div key={s} className="border border-zinc-900 p-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">{s}</div>
          <div className="text-lg font-black text-white mt-0.5">—</div>
        </div>
      ))}
    </div>
    <button onClick={() => window.open('https://www.ixl.com', '_blank', 'noopener')} className="mt-auto border-2 border-green-500 text-green-400 hover:bg-green-500 hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest w-full">Open IXL</button>
  </WidgetShell>
);

const CombineCard = ({ nav, unlocked }) => (
  <WidgetShell title="SAA Combine" icon={Target} unlocked={unlocked} onOpen={() => nav('/academy/combine')} cta="Practice">
    <div className="text-sm text-white font-black">5-Rep Sets</div>
    <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">Deterministic Sport IQ questions pulled from Coach B's glossary. Difficulty auto-adjusts.</p>
    <button onClick={() => nav('/academy/combine')} className="mt-auto bg-orange-500 hover:bg-orange-400 text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest w-full">
      Start a Set
    </button>
  </WidgetShell>
);

const SportIQCard = ({ nav, unlocked }) => (
  <WidgetShell title="Sport IQ · Coach B" icon={GraduationCap} accent="#D4AF37" unlocked={unlocked} onOpen={() => nav('/academy/elective-courses')} cta="Courses">
    <div className="text-sm text-white font-black mb-2">The IQ Courses</div>
    <div className="grid grid-cols-2 gap-1.5 mt-auto">
      <button onClick={() => nav('/basketball-iq/course')} className="border border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black px-2 py-1.5 text-[9px] font-black uppercase tracking-widest">Basketball</button>
      <button onClick={() => nav('/academy/elective-courses')} className="border border-zinc-700 text-zinc-400 hover:border-amber-500 hover:text-amber-400 px-2 py-1.5 text-[9px] font-black uppercase tracking-widest">Football</button>
      <button onClick={() => nav('/academy/elective-courses')} className="border border-zinc-700 text-zinc-400 hover:border-amber-500 hover:text-amber-400 px-2 py-1.5 text-[9px] font-black uppercase tracking-widest">Soccer</button>
      <button onClick={() => nav('/academy/elective-courses')} className="border border-zinc-700 text-zinc-400 hover:border-amber-500 hover:text-amber-400 px-2 py-1.5 text-[9px] font-black uppercase tracking-widest">Track</button>
    </div>
  </WidgetShell>
);

const SocraticAI = ({ nav, unlocked }) => (
  <WidgetShell title="Ask Coach · AI" icon={Brain} accent="#8B5CF6" unlocked={unlocked} onOpen={() => nav('/academy/school')} cta="Chat">
    <p className="text-[11px] text-zinc-300 leading-relaxed">Ask Coach — the Socratic AI — a homework question, or drill Sport IQ scenarios. It answers with questions, not answers.</p>
    <button onClick={() => nav('/academy/school')} className="mt-auto border-2 border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest w-full">Open Coach Chat</button>
  </WidgetShell>
);

const PERAnalytics = ({ nav, unlocked }) => (
  <WidgetShell title="PER Analytics" icon={Zap} accent="#EAB308" unlocked={unlocked} onOpen={() => nav('/academy/school')} cta="Report">
    <div className="grid grid-cols-3 gap-2 text-center">
      {[['MASTERY', '—'], ['STREAK', '0'], ['REPS', '0']].map(([k, v]) => (
        <div key={k}>
          <div className="text-2xl font-black text-white">{v}</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{k}</div>
        </div>
      ))}
    </div>
    <p className="text-[10px] text-zinc-600 mt-auto pt-3">PER Analytics summarizes weekly performance across academics + Sport IQ + Combine.</p>
  </WidgetShell>
);

const ForumFeed = ({ nav, unlocked }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get('/academy/forum/boards/the-court/threads?limit=5', { headers: authHeaders() })
      .then((r) => setItems(r.data.threads || []))
      .catch(() => {});
  }, []);
  return (
    <WidgetShell title="Playbook Boards" icon={MessagesSquare} accent="#3B82F6" unlocked={unlocked} onOpen={() => nav('/community')} cta="All Boards">
      {items.length === 0 ? <p className="text-[11px] text-zinc-600">No threads yet.</p> :
        <div className="space-y-2">
          {items.map((t) => (
            <button key={t.id} onClick={() => nav(`/community/thread/${t.id}`)} className="w-full text-left border border-zinc-900 hover:border-orange-500 p-2 transition-colors">
              <div className="text-[11px] font-black text-white truncate">{t.title}</div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-widest mt-0.5">by {t.author_name} · {t.reply_count || 0} replies</div>
            </button>
          ))}
        </div>
      }
    </WidgetShell>
  );
};

const CalendarWidget = ({ nav, unlocked }) => (
  <WidgetShell title="Calendar · Travel / Absences" icon={CalendarDays} accent="#EC4899" unlocked={unlocked} onOpen={() => nav('/academy/calendar')} cta="Open">
    <p className="text-[11px] text-zinc-400 leading-relaxed">No absences logged in the next 14 days. Add travel & tournament conflicts from the Calendar.</p>
    <button onClick={() => nav('/academy/calendar')} className="mt-auto border-2 border-pink-500 text-pink-400 hover:bg-pink-500 hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest w-full">
      Log Absence
    </button>
  </WidgetShell>
);

const WIDGETS = {
  block_tracker:  BlockTracker,
  ixl_radar:      IXLRadar,
  socratic_ai:    SocraticAI,
  quote_banner:   QuoteBanner,
  forum_feed:     ForumFeed,
  calendar:       CalendarWidget,
  per_analytics:  PERAnalytics,
  combine:        CombineCard,
  sport_iq:       SportIQCard,
};

// ─── Page ────────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const [layout, setLayout] = useState([]);
  const [defaultLayout, setDefaultLayout] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/academy/dashboard/layout', { headers: authHeaders() }),
      api.get('/academy/auth/me', { headers: authHeaders() }).catch(() => ({ data: { user: null } })),
    ])
      .then(([lay, me]) => {
        setLayout(lay.data.layout || []);
        setDefaultLayout(lay.data.default || []);
        setMe(me.data.user);
      })
      .catch(() => toast.error('Could not load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const saveLayout = useCallback(async (next) => {
    setSaving(true);
    try {
      await api.put('/academy/dashboard/layout', { layout: next }, { headers: authHeaders() });
    } catch { /* silent — retry next drag */ }
    finally { setSaving(false); }
  }, []);

  const onLayoutChange = (next) => {
    setLayout(next);
    // debounce-lite: save only on the trailing edge (drag stop fires layout-change too)
  };

  const resetLayout = async () => {
    if (!window.confirm('Reset dashboard to the SAA default layout?')) return;
    try {
      const r = await api.post('/academy/dashboard/reset', {}, { headers: authHeaders() });
      setLayout(r.data.layout || defaultLayout);
      toast.success('Dashboard reset');
    } catch { toast.error('Reset failed'); }
  };

  const items = useMemo(() => layout.filter((it) => WIDGETS[it.i]), [layout]);

  // react-grid-layout won't clamp `w` down to the breakpoint's col count on its
  // own — so a widget authored at w:12 (desktop) blows past a 2-col mobile grid
  // and produces horizontal overflow. Build a layout-per-breakpoint that
  // clamps every item to fit inside its breakpoint's col count.
  const layoutsPerBp = useMemo(() => {
    const bps = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };
    const out = {};
    for (const [bp, cols] of Object.entries(bps)) {
      out[bp] = items.map((it) => ({
        ...it,
        w: Math.min(it.w, cols),
        x: Math.min(it.x, Math.max(0, cols - Math.min(it.w, cols))),
      }));
    }
    return out;
  }, [items]);

  // Track container width — react-grid-layout 2.x requires an explicit width.
  // Start at 0 so RGL doesn't render items at 1200px on a narrow phone before
  // ResizeObserver fires; we skip rendering the grid until we have a real
  // measurement.
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [loading]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24 overflow-x-hidden" data-testid="dashboard">
      <div className="border-b-2 border-zinc-900 py-6 px-6">
        <div className="container mx-auto max-w-7xl flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">Master The Play</div>
            <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-tight mt-1">
              {me?.name ? me.name.split(' ')[0] : 'Athlete'}'s IQ
            </h1>
            <p className="text-orange-400/80 text-xs md:text-sm font-black uppercase tracking-widest mt-2 max-w-2xl">Logical reasoning · memory · puzzle solving · processing speed</p>
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="text-[10px] text-zinc-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
            <button onClick={() => setUnlocked((v) => !v)}
              className={`border-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-colors ${unlocked ? 'border-orange-500 bg-orange-500 text-black hover:bg-orange-400' : 'border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-400'}`}
              data-testid="layout-lock-toggle">
              {unlocked ? <><Unlock className="w-3 h-3" /> Unlocked · Tap to Lock</> : <><Lock className="w-3 h-3" /> Locked · Tap to Customize</>}
            </button>
            {unlocked && (
              <button onClick={resetLayout} className="border-2 border-zinc-800 hover:border-orange-500 text-zinc-400 hover:text-orange-400 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5" data-testid="reset-layout-btn">
                <RefreshCcw className="w-3 h-3" /> Reset Default
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 py-6">
        <div ref={containerRef}>
          {width > 0 && (
            <ResponsiveGrid
            className="layout"
            layouts={layoutsPerBp}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={70}
            margin={[12, 12]}
            containerPadding={[0, 0]}
            width={width}
            onLayoutChange={onLayoutChange}
            /* react-grid-layout v2.2.4 EventCallback signature:
               (layout, oldItem, newItem, placeholder, event, element).
               At onDragStop, the `layout` arg still lists the moved item
               at its PRE-drag x/y — only `newItem` reflects the dropped
               position. Splice newItem into current layout so React state
               (and the backend PUT) get the actual new coords; without this
               the tile snaps back on mouseup. */
            onDragStop={(_layout, _old, newItem) => {
              if (!newItem) return;
              setLayout((prev) => {
                const next = prev.map((it) =>
                  it.i === newItem.i
                    ? { ...it, x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h }
                    : it
                );
                saveLayout(next);
                return next;
              });
            }}
            onResizeStop={(_layout, _old, newItem) => {
              if (!newItem) return;
              setLayout((prev) => {
                const next = prev.map((it) =>
                  it.i === newItem.i
                    ? { ...it, x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h }
                    : it
                );
                saveLayout(next);
                return next;
              });
            }}
            /* react-grid-layout v2.2.4 API: DragConfig = { enabled, handle, ... }
               When `unlocked` is false, drag is fully disabled so button
               clicks fire normally. When true, only the .drag-handle grip
               inside each widget header initiates a drag. */
            dragConfig={{ enabled: unlocked, handle: '.drag-handle' }}
            resizeConfig={{ enabled: unlocked }}
          >
            {items.map((it) => {
              const W = WIDGETS[it.i];
              return (
                <div key={it.i} data-testid={`widget-${it.i}`}>
                  <W nav={navigate} unlocked={unlocked} />
                </div>
              );
            })}
          </ResponsiveGrid>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
